import _get from 'lodash/get'
import { z } from 'zod'

import { HOUSEHOLD_STEPS } from '@flows/configs/householdAt/constants'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { FAILURE_STEP_NAME } from '@flows/insurance/constants'

const household_at_statement_of_fact = (params: StepConfigParams) =>
  ({
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getParamPath('statement_of_facts.property_is_primary_residency')]:
        z.boolean(),
      [params.helperFunctions.getParamPath('statement_of_facts.constant_occupation')]: z.boolean(),
      [params.helperFunctions.getParamPath(
        'statement_of_facts.natural_disasters_damage_in_last_10_years'
      )]: z.boolean(),
      [params.helperFunctions.getParamPath('statement_of_facts.business_use')]: z.boolean(),
      [params.helperFunctions.getParamPath('statement_of_facts.cancelled_by_previous_insurer')]:
        z.boolean(),
    }),
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_householdAt:steps.statement_of_fact.title',
        },
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_householdAt:steps.statement_of_fact.permanentResidence',
        },
      },
      {
        type: 'choice',
        shouldTrackValues: true,
        as: 'chip',
        name: params.helperFunctions.getParamPath(
          'statement_of_facts.property_is_primary_residency'
        ),
        options: [
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:generic.inputs.true',
            },
            value: true,
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:generic.inputs.false',
            },
            value: false,
          },
        ],
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_householdAt:steps.statement_of_fact.occupiedNinthMonths',
        },
      },
      {
        type: 'choice',
        as: 'chip',
        name: params.helperFunctions.getParamPath('statement_of_facts.constant_occupation'),
        options: [
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:generic.inputs.true',
            },
            value: true,
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:generic.inputs.false',
            },
            value: false,
          },
        ],
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey:
            'sharedFlows_householdAt:steps.statement_of_fact.loss_insured_location_due_flood',
        },
      },
      {
        type: 'choice',
        as: 'chip',
        name: params.helperFunctions.getParamPath(
          'statement_of_facts.natural_disasters_damage_in_last_10_years'
        ),
        options: [
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:generic.inputs.true',
            },
            value: false,
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:generic.inputs.false',
            },
            value: true,
          },
        ],
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_householdAt:steps.statement_of_fact.businessPurpose',
        },
      },
      {
        type: 'choice',
        as: 'chip',
        name: params.helperFunctions.getParamPath('statement_of_facts.business_use'),
        options: [
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:generic.inputs.true',
            },
            value: false,
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:generic.inputs.false',
            },
            value: true,
          },
        ],
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey:
            'sharedFlows_householdAt:steps.statement_of_fact.cancelled_by_previous_insurer',
        },
      },
      {
        type: 'choice',
        as: 'chip',
        name: params.helperFunctions.getParamPath(
          'statement_of_facts.cancelled_by_previous_insurer'
        ),
        options: [
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:generic.inputs.true',
            },
            value: false,
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_common:generic.inputs.false',
            },
            value: true,
          },
        ],
      },
    ],
    subFlow: params.subFlow,
    nextStepConfig: {
      valueFunction: (flowValues) => {
        const isAllStatementsTrue =
          _get(
            flowValues,
            params.helperFunctions.getParamPath('statement_of_facts.property_is_primary_residency')
          ) &&
          _get(
            flowValues,
            params.helperFunctions.getParamPath('statement_of_facts.constant_occupation')
          ) &&
          _get(
            flowValues,
            params.helperFunctions.getParamPath('statement_of_facts.business_use')
          ) === false &&
          _get(
            flowValues,
            params.helperFunctions.getParamPath('statement_of_facts.cancelled_by_previous_insurer')
          ) === false &&
          _get(
            flowValues,
            params.helperFunctions.getParamPath(
              'statement_of_facts.natural_disasters_damage_in_last_10_years'
            )
          ) === false

        if (isAllStatementsTrue) {
          return HOUSEHOLD_STEPS.postQuoteStep
        }
        return FAILURE_STEP_NAME
      },
      valueToStepName: {
        postQuoteStep: HOUSEHOLD_STEPS.postQuoteStep,
        failure: FAILURE_STEP_NAME,
      },
    },
  }) as const satisfies AnyStepConfiguration

export default household_at_statement_of_fact
